<template>
	<div formGroup="TicketForm" class="h-full flex justify-center">
		<div class="flex flex-col h-full w-full max-w-xl">
			<panel addclass="mt-4">
				<div id="ticket-form" class="flex flex-col items-center pt-6">
					<validation cssClass="w-full" v-show="isAdmin && data.isAdd">
						<ejs-combobox ref='type' name='type' :dataSource='labels.types' :fields='fields' floatLabelType="Always" :placeholder="labels.titles.type" v-model:value="data.type"
							:showClearButton="false" :allowCustom="false" :change="OnTypeChanged"></ejs-combobox>
					</validation>
					<!-- firms -->
					<validation cssClass="w-full max-w-text" v-show="isAdmin && data.isAdd">
						<ejs-combobox ref='firma' name='firma' :dataSource='firms' :fields='firm_fields' floatLabelType="Always" :placeholder="labels.titles.firm" v-model:value="data.firm_name"
							:showClearButton="false" :allowCustom="false" :change="OnFirmChanged"></ejs-combobox>
					</validation>
					<!-- users -->
					<validation cssClass="w-full max-w-text" v-show="data.isAdd && showName">
						<ejs-combobox ref='name' id="name" name='name' :dataSource='users' :fields='firm_fields' floatLabelType="Always" :placeholder="labels.titles.name" v-model:value="data.name"
							:showClearButton="false" :allowCustom="false"></ejs-combobox>
					</validation>
					<!-- requests -->
					<validation cssClass="w-full max-w-text" v-show="data.isAdd && !isCustomer">
						<ejs-combobox ref='request' id='request_id' name='request_id' :dataSource='requests' :fields='firm_fields' floatLabelType="Always" :placeholder="labels.titles.request"
							v-model:value="data.request_id" :showClearButton="false" :allowCustom="false" :allowFiltering="true" :filtering="onFiltering"></ejs-combobox>
					</validation>
					<validation cssClass="w-full max-w-text">
						<ejs-combobox ref='prio' name='prio' :dataSource='labels.prios' :fields='fields' floatLabelType="Always" :placeholder="labels.titles.prio" v-model:value="data.prio"
							:showClearButton="false" :allowCustom="false"></ejs-combobox>
					</validation>
					<validation cssClass="w-full max-w-text" v-show="isAdmin">
						<ejs-combobox id='status' name='status' :dataSource='labels.statuses' :fields='fields' floatLabelType="Always" :placeholder="labels.titles.status" v-model:value="data.status"
							:showClearButton="false" :allowCustom="false"></ejs-combobox>
					</validation>
					<validation cssClass="w-full max-w-text" v-show="isAdmin">
						<ejs-datepicker id="due_date" name="due_date" v-model="data.due_date" format="dd.MM.yyyy"  floatLabelType="Always" :placeholder="labels.titles.due_date"></ejs-datepicker>
					</validation>
					<div class="eny-radio-toolbar max-w-[1600px] flex justify-center items-center flex-col sm:flex-row pt-4">
						<div v-show="isAdmin" v-for="chanel in labels.chanels" :key="chanel.value" class="eny-radio">
							<input type="radio" :id="'chanel_' + chanel.value" name="chanel" :value=chanel.value v-model="data.chanel" />
							<label :for="'chanel_' + chanel.value"><span>{{chanel.label}}</span></label>
						</div>
					</div>
					<validation cssClass="w-full max-w-text pt-2">
						<ejs-textbox :multiline="true" name="text" v-model:value="data.text" floatLabelType="Always" :placeholder="labels.titles.text"></ejs-textbox>
					</validation>
				</div>
			</panel>
		</div>
	</div>
</template>

<script>
import { TextBoxComponent, FormValidator } from '@syncfusion/ej2-vue-inputs';
import { DatePickerComponent } from "@syncfusion/ej2-vue-calendars";
import { ComboBoxComponent, VirtualScroll } from "@syncfusion/ej2-vue-dropdowns";
import Panel from "../../Base/Panel.vue";
import { computed } from 'vue'
import app from '../../../app';
import Validation from "../../Base/Validation.vue";
import useUtils from '../../hooks/useUtils';

export default {
	name: "TicketDialog",
	components: {
		'panel': Panel,
		'ejs-datepicker': DatePickerComponent,
		'ejs-combobox': ComboBoxComponent,
		'ejs-textbox': TextBoxComponent,
		'validation': Validation,
	},
	provide: {
		combobox: [VirtualScroll]
	},
	setup() {
		const kc = app.config.globalProperties.$keycloak;
		const store = app.config.globalProperties.$store;
		const labels = computed(() => store.getters['ticket/labels']);
		const isAdmin = computed(() => kc.hasRealmRole('admin'));
		const isCustomer = computed(() => kc.hasRealmRole('customer'));
		const getFirmList = () => store.dispatch('firm/getFirmList');
		const firms = computed(() => store.getters['firm/firm']);
		const getUsersList = (role, firmId) => store.dispatch('user/users', {firmId: firmId, role: role});
		const users = computed(() => store.getters['user/users']);
		const user = computed(() => store.getters['user/user']);
		const getRequests = (firmId) => store.dispatch('user/firmRequests', firmId);
		const requests = computed(() => store.getters['user/requests']);
		const setValidator = (validator) => store.commit('ticket/UPDATE_VALIDATOR', validator);
		const validator = computed(() => store.getters['ticket/validator']);
		const fields = { text: 'label', value: 'value' };
		const firm_fields = { text: 'name', value: 'id' };
		const { isFormattedDateValid } = useUtils();
		return {
			store,
			labels,
			isAdmin,
			getFirmList,
			firms,
			getUsersList,
			users,
			user,
			getRequests,
			requests,
			isCustomer,
			setValidator,
			validator,
			fields,
			firm_fields,
			isFormattedDateValid
		}
	},
	data() {
		return {
			data: {},
			showName: false,
		};
	},
	mounted() {
		//define default values
		if (this.data.isAdd) {
			this.data.prio='middle';
			this.data.type='customer';
			this.data.status='new';
			this.data.chanel='extern';
			if (this.isAdmin) {
				this.data.chanel='intern';
				if (!this.firms.length) {
					this.getFirmList(); // load firm list for admins
				}
			}
			//load custom requests for merchants
			else if (!this.isCustomer) {
				this.getRequests(this.user.firm_id);
			}
		}
		// configure ticket form
		let ticketFormOptions = {
			rules: {
				name: { required: true },
				prio : { required: true },
				status : { required: true },
				request_id : { required: true },
				text : { required: true },
				due_date: { custom: [this.isValidDate, this.$t('eny.date_not_valid_msg')] },
			},
			customPlacement: this.showValidationError,
		};
		this.setValidator(new FormValidator("#ticket-form", ticketFormOptions));
	},
	methods: {
		showValidationError: function(element, errorElement) {
			const el=element.closest('.validation-group').querySelector('.error-element');
			if (el && errorElement) {
				el.appendChild(errorElement);
			}
		},
		OnTypeChanged(argv) {
			this.showName=(argv.value=='merchant');
			if (this.showName) {
				const selFirm=this.$refs.firma.ej2Instances.value;
				this.loadUsers(selFirm);
			}
		},
		OnFirmChanged(argv) {
			const selFirm=this.$refs.firma.ej2Instances.value;
			this.loadUsers(selFirm);
			if (this.data.isAdd && selFirm) {
				this.$refs.request.ej2Instances.value="";
				this.getRequests(selFirm);
			}
		},
		loadUsers(selFirm) {
			const selType=this.$refs.type.ej2Instances.value;
			if (this.showName && this.isAdmin && selFirm) {
				this.$refs.name.ej2Instances.value="";
				this.getUsersList(selType, selFirm);
			}
		},
		isValidDate(input) {
			return this.isFormattedDateValid(input.value, this.$t('eny.moment_date_format'));
		},
		onFiltering: debounce(function(e) {
			const query = new this.$refs.request.ej2Instances.query(); // Access ComboBox instance
			query.where('name', 'contains', e.text, true); // Filter logic
			e.updateData(this.requests, query);
		}, 300) // Debounce delay: 300ms
	}
}
function debounce(func, wait) {
	let timeout;
	return function(...args) {
		clearTimeout(timeout);
		timeout = setTimeout(() => func.apply(this, args), wait);
	};
}
</script>

<style>
.e-grid .e-rowcell{
	font-size: 12px;
}
</style>
